import { useCallback, useMemo } from "react";
import { Copy, Download } from "lucide-react";
import { toast } from "react-toastify";
import { useTranslation } from "react-i18next";

import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "./ui/dialog";
import { Button } from "./ui/button";

type ApiKeyDialogData = {
  name: string;
  expires_at?: string | null;
  key?: string | null;
  secret_key?: string | null;
};

interface ApiKeySuccessDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: string;
  apiKey: ApiKeyDialogData | null;
  note: string;
}

const ApiKeySuccessDialog = ({
  open,
  onOpenChange,
  title,
  apiKey,
  note,
}: ApiKeySuccessDialogProps) => {
  const { t } = useTranslation();
  const keyValue = useMemo(
    () => apiKey?.secret_key ?? apiKey?.key ?? "",
    [apiKey],
  );

  const handleCopyKey = useCallback(() => {
    if (!keyValue) {
      return;
    }

    navigator.clipboard.writeText(keyValue);
    toast.success(t("apiKeyDialog.apiKeyCopied"));
  }, [keyValue, t]);

  const handleDownload = useCallback(() => {
    if (!apiKey || !keyValue) {
      return;
    }

    const content = `${t("apiKeyDialog.txtDetailsTitle")}\n===============\n${t("apiKeyDialog.keyName")}: ${apiKey.name}\n${t("apiKeyDialog.apiKey")}: ${keyValue}\n${t("apiKeys.expires")}: ${apiKey.expires_at ? new Date(apiKey.expires_at).toLocaleString() : t("common.never")}\n${t("apiKeyDialog.timestamp")}: ${new Date().toISOString()}\n\n${note}`;

    const blob = new Blob([content], { type: "text/plain" });
    const url = URL.createObjectURL(blob);
    const anchor = document.createElement("a");
    anchor.href = url;
    anchor.download = `${apiKey.name}-api-key.txt`;
    document.body.appendChild(anchor);
    anchor.click();
    document.body.removeChild(anchor);
    URL.revokeObjectURL(url);
  }, [apiKey, keyValue, note, t]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-card border-border sm:max-w-lg">
        <DialogHeader>
          <DialogTitle className="text-foreground text-xl font-bold">
            {title}
          </DialogTitle>
          <DialogDescription className="sr-only">
            {t("apiKeyDialog.keyDetails")}
          </DialogDescription>
        </DialogHeader>

        <div className="flex flex-col gap-5 pt-2">
          <div className="flex flex-col gap-1.5">
            <label className="text-primary text-sm font-medium">
              {t("apiKeyDialog.keyName")}
            </label>
            <p className="text-foreground text-base font-medium">
              {apiKey?.name}
            </p>
          </div>

          <div className="flex flex-col gap-1.5">
            <label className="text-primary text-sm font-medium">
              {t("apiKeyDialog.apiKey")}
            </label>
            <div className="flex items-center rounded-[8px] border border-border bg-input px-4 py-3">
              <code className="text-foreground flex-1 break-all font-mono text-sm max-h-[100px] overflow-y-auto">
                {keyValue || t("apiKeyDialog.keyValueNotReturned")}
              </code>
              <button
                type="button"
                onClick={handleCopyKey}
                className="text-muted-foreground hover:text-foreground ml-3 rtl:ml-0 rtl:mr-3 shrink-0 transition-colors disabled:opacity-40"
                aria-label={t("apiKeyDialog.apiKey")}
                disabled={!keyValue}
              >
                <Copy className="size-5" />
              </button>
            </div>
            <p className="text-primary/70 text-sm">{note}</p>
          </div>
        </div>

        <DialogFooter className="pt-4">
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            className="border-border bg-card text-foreground hover:bg-accent px-6"
          >
            {t("common.close")}
          </Button>
          <Button
            onClick={handleDownload}
            className="bg-primary text-primary-foreground hover:bg-primary/90 gap-2 px-6"
            disabled={!keyValue}
          >
            <Download className="size-4" />
            {t("apiKeyDialog.downloadAsTxt")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default ApiKeySuccessDialog;
